// src/lib/hooks/useVisibilityObserver.ts

"use client"

/**
 * Lightweight IntersectionObserver wrapper that powers deferred rendering.
 *
 * We use this hook for expensive MDX visualizations so that Recharts and the
 * data generation helpers are not executed until the chart scrolls into view.
 * This prevents the blog detail route from allocating large arrays during
 * initial hydration and keeps scroll performance smooth on low-power devices.
 */
import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Configuration options for the visibility observer.
 *
 * @typedef {Object} VisibilityObserverOptions
 * @property {string} [rootMargin] - Margin passed to the underlying IntersectionObserver.
 *   Defaults to "200px 0px" to trigger slightly before the element enters the viewport.
 * @property {number | number[]} [threshold] - Intersection thresholds (0-1) before firing callbacks.
 *   Defaults to 0, meaning the callback fires as soon as any pixel is visible.
 * @property {boolean} [once] - Whether to stop observing after the first intersection.
 *   Defaults to true for performance optimization.
 * @property {boolean} [disabled] - Forces the observer into the "visible" state immediately.
 *   Useful for server-side rendering or testing scenarios.
 */
export interface VisibilityObserverOptions {
  rootMargin?: string
  threshold?: number | number[]
  once?: boolean
  disabled?: boolean
}

/**
 * Describes the visibility state returned by the hook.
 *
 * @typedef {Object} VisibilityObserverState
 * @property {boolean} isIntersecting - Whether the element is currently in view.
 * @property {boolean} hasEntered - Whether the element has intersected at least once.
 *   This flag never resets to false, making it ideal for triggering one-time renders.
 */
export interface VisibilityObserverState {
  isIntersecting: boolean
  hasEntered: boolean
}

/**
 * React hook that defers expensive client work until an element becomes visible.
 *
 * The hook intentionally exposes a ref callback rather than a raw ref so we can
 * recreate the IntersectionObserver whenever the options change without leaking
 * listeners. We eagerly bail out when IntersectionObserver is unavailable so
 * server-side rendering and legacy browsers simply render the content inline.
 *
 * @template T - The type of DOM element to observe, defaults to Element.
 * @param {VisibilityObserverOptions} [options={}] - Configuration for the observer behavior.
 * @returns {{ref: (node: T | null) => void, state: VisibilityObserverState}}
 *   An object containing a ref callback to attach to the target element and the current visibility state.
 *
 * @example
 * ```tsx
 * const { ref, state } = useVisibilityObserver({ once: true })
 * return (
 *   <div ref={ref}>
 *     {state.hasEntered && <ExpensiveChart />}
 *   </div>
 * )
 * ```
 */
export function useVisibilityObserver<T extends Element = Element>(
  options: VisibilityObserverOptions = {},
): {
  ref: (node: T | null) => void
  state: VisibilityObserverState
} {
  // Destructure options with sensible defaults for performance
  // rootMargin triggers 200px before viewport entry to preload content smoothly
  const {
    rootMargin = "200px 0px",
    threshold = 0,
    once = true,
    disabled = false,
  } = options

  // Track the DOM element we're observing - null until ref callback is invoked
  const [target, setTarget] = useState<T | null>(null)
  
  // Store observer instance in a ref to persist across renders without triggering re-renders
  // This prevents unnecessary observer recreation and improves performance
  const observerRef = useRef<IntersectionObserver | null>(null)
  
  // Initialize state based on disabled flag - if disabled, treat as immediately visible
  // This optimization prevents unnecessary observer setup when content should render immediately
  const [state, setState] = useState<VisibilityObserverState>(() => ({
    isIntersecting: disabled,
    hasEntered: disabled,
  }))

  // Memoized cleanup function to prevent memory leaks
  // Wrapped in useCallback to maintain referential equality across renders
  const cleanupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
  }, [])

  // Main effect that manages the IntersectionObserver lifecycle
  // Dependencies ensure observer is recreated only when necessary configuration changes
  useEffect(() => {
    // Early return if no target element is attached yet
    if (!target) {
      cleanupObserver()
      return
    }

    // Bail out for SSR, disabled state, or unsupported browsers
    // Setting state to visible ensures content renders immediately as fallback
    if (
      disabled ||
      typeof window === "undefined" ||
      !("IntersectionObserver" in window)
    ) {
      cleanupObserver()
      // eslint-disable-next-line react-hooks/set-state-in-effect -- setState runs in the IntersectionObserver callback, not synchronously in the effect
      setState({ isIntersecting: true, hasEntered: true })
      return
    }

    // Create new observer with callback that updates visibility state
    const observer = new IntersectionObserver(
      (entries) => {
        // IntersectionObserver always provides at least one entry
        const entry = entries[0]
        if (!entry) {
          return
        }

        const { isIntersecting } = entry
        
        // Update state immutably - hasEntered is sticky (never resets to false)
        // This allows components to render once and stay rendered even if scrolled away
        setState((prev) => ({
          isIntersecting,
          hasEntered: prev.hasEntered || isIntersecting,
        }))

        // Performance optimization: stop observing after first intersection if once=true
        // Reduces ongoing intersection calculations for one-time render scenarios
        if (isIntersecting && once) {
          observer.unobserve(entry.target)
        }
      },
      { rootMargin, threshold },
    )

    // Start observing the target element
    observer.observe(target)
    observerRef.current = observer

    // Cleanup function runs when dependencies change or component unmounts
    // Prevents memory leaks by disconnecting observer
    return () => {
      cleanupObserver()
    }
  }, [cleanupObserver, disabled, once, rootMargin, target, threshold])

  // Ref callback pattern allows us to track when the element is mounted/unmounted
  // Memoized with useCallback to prevent unnecessary re-renders of child components
  const ref = useCallback((node: T | null) => {
    setTarget(node)
  }, [])

  return { ref, state }
}
